import { Injectable, signal, type Signal } from '@angular/core';
import { CurrentAccountService } from '../current-account.service';

/**
 * Framework-default implementation of the `CurrentAccountService` port for scaffolds that
 * have no IAM bounded context (e.g. none-mode install, clean scaffold before `catalyst install iam`).
 *
 * Provided in the published scaffold's `aurora.provider.ts` by the packager step in
 * `scripts/packager/frontend/application.mjs` so the authenticated initializer resolves
 * and the shell boots with a neutral anonymous identity. The catalyst SOURCE `aurora.provider.ts`
 * does NOT register this provider — the two placements never coexist.
 *
 * The port contract (`displayName`, `email`, `permissions`, `permissionsLoaded`, `hasPermission()`,
 * `load()`) is satisfied with blank/empty signals and a no-op `load()`. The sidebar renders a
 * neutral placeholder rather than a fabricated identity.
 *
 * ## Permission surface
 *
 * `permissions` is always empty and `permissionsLoaded` is always `true` — there is no async load
 * to await, so the readiness gate resolves immediately. This prevents the sidebar and the permission
 * guard from stalling forever when no IAM adapter is wired. Every `hasPermission()` call returns
 * `false` (the anonymous session holds no permissions).
 *
 * This mirrors the backend `AnonymousAccountLoaderAdapter` pattern.
 *
 * HR-PORTS-001 compliance: imports only `@angular/core` and the port; zero `@app`/`@api`/
 * bounded-context coupling.
 */
@Injectable()
export class AnonymousCurrentAccountAdapter extends CurrentAccountService {
  readonly displayName = signal('');
  readonly email = signal('');
  /** Anonymous session has no permissions. Readonly empty array for the lifetime of the session. */
  readonly permissions = signal<readonly string[]>([]);
  /**
   * Resolves `true` immediately — the anonymous adapter has no async load to await, so consumers
   * must not stall waiting for a readiness signal that would never flip.
   */
  readonly permissionsLoaded = signal(true);
  /** No role, no preference: the landing resolution falls back to the application root. */
  readonly defaultRedirection = signal<string | null>(null);
  /** No role hides the navigation: the sidebar state is left untouched by the anonymous adapter. */
  readonly hasHiddenVerticalNavigation = signal(false);
  /**
   * Resolves `true` immediately, mirroring `permissionsLoaded` — no async load to await, so the
   * landing resolution must not stall waiting for a readiness signal that would never flip.
   */
  readonly loaded = signal(true);
  /** No account is ever loaded, so there is no id, no photo and nothing to take initials from. */
  readonly accountId = signal<string | null>(null);
  readonly avatarSrc = signal<string | null>(null);
  readonly initials = signal('');

  /**
   * Opens nothing: the profile dialog belongs to the IAM bounded context, which this adapter
   * stands in for. The user menu still renders the "Account" item; it simply does nothing.
   */
  openProfile(): void {
    // No-op by contract.
  }

  /**
   * Always returns `false`. The anonymous session holds no permissions and no query is ever
   * issued to populate them.
   */
  hasPermission(_permission: string): boolean {
    return false;
  }

  /** Always empty: an anonymous session has no `meta`. */
  metaList(_key: string): Signal<readonly unknown[]> {
    return signal<readonly unknown[]>([]);
  }

  addMetaItems(_key: string, _items: unknown): void {
    // No-op: an anonymous session has nowhere to persist it.
  }

  removeMetaItems(_key: string, _items: unknown): void {
    // No-op: an anonymous session has nowhere to persist it.
  }

  load(): void {
    // No-op: no IAM bounded context is available, so no account can be loaded.
    // The signals stay blank (anonymous identity) throughout the session.
    // permissionsLoaded stays true (no async work to wait for).
  }
}
